interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: {
    user?: { id?: number; language_code?: string; first_name?: string };
  };
  ready(): void;
  expand(): void;
  close(): void;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  BackButton: {
    show(): void;
    hide(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
  MainButton: {
    setParams(params: {
      text?: string;
      is_active?: boolean;
      is_visible?: boolean;
      color?: string;
      text_color?: string;
    }): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  };
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  showAlert(message: string, callback?: () => void): void;
  setHeaderColor?(color: string): void;
  disableVerticalSwipes?(): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function webApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export const isInsideTelegram = (): boolean => Boolean(webApp()?.initData);

export function initTelegram(): void {
  const app = webApp();
  if (!app) return;

  app.ready();
  app.expand();
  app.disableVerticalSwipes?.();
  applyColorScheme(app.colorScheme);
}

function applyColorScheme(scheme: 'light' | 'dark'): void {
  document.documentElement.dataset.theme = scheme;
  document.documentElement.style.colorScheme = scheme;
}

export function getInitData(): string | null {
  const raw = webApp()?.initData;
  return raw && raw.length > 0 ? raw : null;
}

export function haptic(type: 'light' | 'medium' | 'heavy' = 'light'): void {
  webApp()?.HapticFeedback?.impactOccurred(type);
}

export function notify(type: 'error' | 'success' | 'warning'): void {
  webApp()?.HapticFeedback?.notificationOccurred(type);
}

export function openExternal(url: string): void {
  const app = webApp();
  if (app) app.openLink(url);
  else window.open(url, '_blank', 'noopener,noreferrer');
}

export function setBackButton(handler: (() => void) | null): () => void {
  const app = webApp();
  if (!app) return () => undefined;

  if (!handler) {
    app.BackButton.hide();
    return () => undefined;
  }

  app.BackButton.onClick(handler);
  app.BackButton.show();

  return () => {
    app.BackButton.offClick(handler);
    app.BackButton.hide();
  };
}
